import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { Body1 } from '@ui/Typography'

import { MasterTransaction } from '@model/MasterTransaction'
import {
  isPaymentProviderBankType,
  isPaymentProviderCardProviderType,
  isPaymentProviderCrypto,
  isPaymentProviderCryptoType,
  isPaymentProviderPSPType,
  isPaymentProviderPaymentAgentType,
} from '@model/PaymentProviderType'

import { TransactionSuccessDialogueTemplate } from '@global/templates/TransactionSuccessDialogueTemplate'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import { WalletWithdrawTransactionFailedPage } from './WalletWithdrawTransactionFailedPage'

type WalletDepositTransactionSuccessState = {
  data: MasterTransaction
  providerCategoryId: number
}

type WalletWithdrawTransactionSuccessPageProps = {
  onCancel(): void
}

type WalletWithdrawFormTransactionSuccessValues = WalletDepositTransactionSuccessState

export const WalletWithdrawTransactionSuccessPage: React.FC<
  WalletWithdrawTransactionSuccessPageProps
> = (props) => {
  const { onCancel } = props

  const { t } = useTranslation()
  const location = useLocation()

  const [values] = useState<WalletDepositTransactionSuccessState>(location.state)
  const providerCategoryId = values?.providerCategoryId

  if (
    isPaymentProviderPSPType(providerCategoryId) ||
    isPaymentProviderCryptoType(providerCategoryId) ||
    isPaymentProviderCardProviderType(providerCategoryId) ||
    isPaymentProviderBankType(providerCategoryId)
  ) {
    return <WalletWithdrawTransactionSuccess />
  }

  if (isPaymentProviderPaymentAgentType(providerCategoryId)) {
    return <WalletWithdrawTransactionSuccessPaymentAgentFactoryPage onCancel={onCancel} />
  }

  if (!values) {
    return (
      <WalletWithdrawTransactionFailedPage
        title={t('Transactions.Funds not withdrawn')}
        onCancel={onCancel}
      />
    )
  }

  return null
}

const WalletWithdrawTransactionSuccess = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()
  const { transaction } = useWalletWithdrawTransactionSuccess()

  const [values] = useState<WalletDepositTransactionSuccessState>(location.state)

  const [walletTransaction] = transaction.walletTransactions
  const paymentProviderName = walletTransaction?.paymentProviderTransaction?.paymentProvider?.name
  const isCrypto = isPaymentProviderCrypto(values?.providerCategoryId, paymentProviderName)

  const isPaymentAgentPath = location.pathname.includes('/dashboard/payment-agent/')
  const isIBPath = location.pathname.includes('/dashboard/introducing-broker/')
  const navigateOptions = {
    state: { hasSuccessfulWithdrawAction: true },
  }

  const handleCancel = () =>
    isPaymentAgentPath
      ? navigate('/dashboard/payment-agent/transaction-history', navigateOptions)
      : isIBPath
      ? navigate('/dashboard/introducing-broker/reports/transactions-history', navigateOptions)
      : navigate('/dashboard/traders-room/transaction-history', navigateOptions)

  const amount =
    transaction.groupReference?.totalPaymentsAmount ??
    transaction.groupReference?.totalWalletsAmount ??
    walletTransaction?.otherAmount
  const paymentAgentTransaction = transaction.walletTransactions.find(
    (walletTransaction) => walletTransaction?.paymentAgentTransaction?.paymentAgent
  )

  return (
    <Loading text={t('Withdrawing...')} isLoading={!transaction} showLoadingIcon>
      <TransactionSuccessDialogueTemplate
        title={t('Wallet.Operation Completed')}
        goBack={handleCancel}
      >
        <Box>
          <Body1 isParagraph>{t('Wallet.From')}</Body1>
          <Body1 isParagraph medium>
            {walletTransaction?.wallet?.currency?.id} {t('Wallet.Wallet')}
          </Body1>
        </Box>
        <Box>
          <Body1 isParagraph>{t('Wallet.Amount')}</Body1>
          <Body1 isParagraph medium>
            {formatMoney(amount, walletTransaction?.otherCurrency?.id, 2, isCrypto)}
          </Body1>
        </Box>
        <Box>
          <Body1 isParagraph>{t('Wallet.Payment Method')}</Body1>
          <Body1 isParagraph className='px-5 has-text-centered' medium>
            {walletTransaction?.paymentProviderTransaction?.paymentProvider?.name ||
              paymentAgentTransaction?.paymentAgentTransaction?.paymentAgent?.name}
          </Body1>
        </Box>
      </TransactionSuccessDialogueTemplate>
    </Loading>
  )
}

const WalletWithdrawTransactionSuccessPaymentAgentFactoryPage: React.FC<{
  onCancel(): void
}> = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { formatMoney } = useFormatNumber()

  const { transaction } = useWalletWithdrawTransactionSuccess()

  const [values] = useState<WalletDepositTransactionSuccessState>(location.state)

  const [walletTransaction] = transaction.walletTransactions
  const paymentProviderName = walletTransaction?.paymentProviderTransaction?.paymentProvider?.name
  const isCrypto = isPaymentProviderCrypto(values?.providerCategoryId, paymentProviderName)

  const isPaymentAgentPath = location.pathname.includes('/dashboard/payment-agent/')
  const navigateOptions = {
    state: { hasSuccessfulWithdrawAction: true },
  }

  const handleCancel = () =>
    isPaymentAgentPath
      ? navigate('/dashboard/payment-agent/transaction-history', navigateOptions)
      : navigate('/dashboard/traders-room/transaction-history', navigateOptions)

  const amount =
    transaction.groupReference?.totalPaymentsAmount ??
    transaction.groupReference?.totalWalletsAmount ??
    walletTransaction?.otherAmount
  const paymentAgentTransaction = transaction.walletTransactions.find(
    (walletTransaction) => walletTransaction?.paymentAgentTransaction?.paymentAgent
  )

  return (
    <Loading text={t('Withdrawing...')} isLoading={!transaction} showLoadingIcon>
      <TransactionSuccessDialogueTemplate
        title={t('Wallet.Operation Completed')}
        goBack={handleCancel}
      >
        <Box>
          <Body1 isParagraph>{t('Wallet.From')}</Body1>
          <Body1 isParagraph medium>
            {walletTransaction?.wallet?.currency?.id} {t('Wallet.Wallet')}
          </Body1>
        </Box>
        <Box>
          <Body1 isParagraph>{t('Wallet.Amount')}</Body1>
          <Body1 isParagraph medium>
            {formatMoney(amount, walletTransaction?.otherCurrency?.id, 2, isCrypto)}
          </Body1>
        </Box>
        <Box>
          <Body1 isParagraph>{t('Wallet.Payment Method')}</Body1>
          <Body1 isParagraph className='px-5 has-text-centered' medium>
            {walletTransaction?.paymentProviderTransaction?.paymentProvider?.name ||
              paymentAgentTransaction?.paymentAgentTransaction?.paymentAgent?.name}
          </Body1>
        </Box>
      </TransactionSuccessDialogueTemplate>
    </Loading>
  )
}

const useWalletWithdrawTransactionSuccess = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [values] = useState<WalletWithdrawFormTransactionSuccessValues>(location.state)

  useEffect(() => {
    navigate({ ...location }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { transaction: values.data }
}
